import { styled } from 'styled-components';

import useIsMobile from 'hooks/useIsMobile';
import { colors } from 'styles/theme/colors';
import LinkIcon from 'svg/LinkIcon';

import LinkedRecordTooltip from './LinkedRecordTooltip';
import useSharedRecordTabs from './useSharedRecordTabs';

const LinkedRecordsBanner = () => {
  const { displayTabs, activeLocationEpisodeId, handleTabClick } = useSharedRecordTabs();
  const isMobile = useIsMobile();

  if (isMobile) return null;
  if (displayTabs.length === 0) return null;

  const linkedCount = displayTabs.length;

  return (
    <Banner data-cy='linked-records-banner'>
      <TabsRow>
        {displayTabs.map((tab) => {
          const isActive = tab.locationEpisodeId === activeLocationEpisodeId;
          const tabButton = (
            <TabButton key={tab.key} $active={isActive} onClick={() => handleTabClick(tab)} data-cy='shared-record-tab'>
              <TabLabel data-cy='shared-record-tab-label'>{tab.label}</TabLabel>
              {tab.subtitle && <TabSubtitle data-cy='shared-record-tab-subtitle'>{tab.subtitle}</TabSubtitle>}
            </TabButton>
          );

          if (isActive || !tab.sharedRecord) return tabButton;

          return (
            <LinkedRecordTooltip key={tab.key} sharedRecord={tab.sharedRecord}>
              {tabButton}
            </LinkedRecordTooltip>
          );
        })}
      </TabsRow>
      <TabsFill>
        <LinkedInfo>
          <LinkIcon width={14} height={14} color={colors.black75} />
          <LinkedText>
            {linkedCount} shared patient {linkedCount === 1 ? 'record' : 'records'}
          </LinkedText>
        </LinkedInfo>
      </TabsFill>
    </Banner>
  );
};

export default LinkedRecordsBanner;

const Banner = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0;
  background-color: ${colors.black05};
`;

const TabsRow = styled.div`
  display: flex;
`;

const TabButton = styled.button<{ $active: boolean }>`
  border: none;
  border-right: 1px solid ${colors.black10};
  border-bottom: ${({ $active }) => ($active ? 'none' : `1px solid ${colors.black10}`)};
  cursor: pointer;
  padding: 12px 24px;
  border-radius: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ $active }) => ($active ? '#fff' : colors.black10)};
  }
`;

const TabsFill = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  align-self: stretch;
  border-bottom: 1px solid ${colors.black10};
`;

const TabLabel = styled.div`
  font-size: 12px;
  color: ${colors.black};
`;

const TabSubtitle = styled.div`
  font-size: 10px;
  color: ${colors.black};
`;

const LinkedInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 24px;

  @media ${({ theme }) => theme.devices.desktop} {
    padding: 0 56px;
  }
`;

const LinkedText = styled.span`
  font-size: 12px;
  color: ${colors.black75};
`;
